import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import * as portalSession from '../../auth/portalSession';
import { getMyFeatureFlags } from '../../utils/featureFlags';
import {
  dispatchMenuNavigate,
  getVisibleSearchItems,
  searchMenuItems
} from '../../utils/menuAccess';
import { nextSearchActiveIndex } from '../../utils/menuSearchNav';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
const MOD_HINT = isMac ? '⌘K' : 'Ctrl+K';

/**
 * Inline header menu search.
 * Real <input> (no transparent overlay) so the caret tracks typed text.
 * Results portal out of the sticky header. Top hit is selected in useLayoutEffect
 * (before paint) so the first row is red as soon as the list appears.
 */
const HeaderSearchBox = ({
  compact = false,
  projectId = null,
  selectedEstimateId = null,
  onSelectDepartment
}) => {
  const { instance, accounts } = useMsal();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const activeIndexRef = useRef(0);
  const resultsRef = useRef([]);
  const permissionsLoadedRef = useRef(false);
  const lastArrowAtRef = useRef(0);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 360 });
  const [userPermissions, setUserPermissions] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [featureFlags, setFeatureFlags] = useState(null);

  const setActive = useCallback((idx) => {
    activeIndexRef.current = idx;
    setActiveIndex(idx);
  }, []);

  const ensurePermissions = useCallback(async () => {
    const hasLogin = !!(accounts[0] || portalSession.isActive());
    if (permissionsLoaded || !hasLogin) return;
    try {
      const [permRes, flags] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info'),
        getMyFeatureFlags(instance, accounts)
      ]);
      if (permRes.data?.success) setUserPermissions(permRes.data.data);
      setFeatureFlags(flags);
    } catch (err) {
      console.error('Menu search permissions failed:', err);
      setUserPermissions({
        menuItems: [],
        isSuperadmin: false,
        isAdmin: false,
        allDepartmentIds: [],
        subDepartments: []
      });
    } finally {
      setPermissionsLoaded(true);
    }
  }, [accounts, instance, permissionsLoaded]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        ensurePermissions();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ensurePermissions]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const visibleItems = useMemo(() => getVisibleSearchItems({
    userPermissions,
    permissionsLoaded,
    featureFlags,
    userEmail: accounts[0]?.username || portalSession.current()?.user?.email,
    projectId,
    selectedEstimateId
  }), [userPermissions, permissionsLoaded, featureFlags, accounts, projectId, selectedEstimateId]);

  const results = useMemo(
    () => searchMenuItems(visibleItems, query),
    [visibleItems, query]
  );
  resultsRef.current = results;
  permissionsLoadedRef.current = permissionsLoaded;

  const showDropdown = open && query.trim().length > 0;
  const listReady = showDropdown && permissionsLoaded && results.length > 0;

  // Before paint: top row selected whenever the list is ready / query changes.
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
    if (listReady) setActive(0);
    else setActive(-1);
  }, [query, listReady, results.length, setActive]);

  useLayoutEffect(() => {
    if (!showDropdown || !wrapRef.current) return undefined;
    const update = () => {
      const rect = wrapRef.current.getBoundingClientRect();
      const width = compact ? Math.min(320, window.innerWidth - 24) : 360;
      let left = compact ? Math.max(12, window.innerWidth - width - 12) : rect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      setPanelPos({ top: rect.bottom + 6, left, width });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showDropdown, compact, query]);

  useEffect(() => {
    const list = listRef.current;
    const el = list?.querySelector(`[data-idx="${activeIndex}"]`);
    if (!list || !el || activeIndex < 0) return;
    const listRect = list.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    if (elRect.top < listRect.top) list.scrollTop -= listRect.top - elRect.top;
    else if (elRect.bottom > listRect.bottom) list.scrollTop += elRect.bottom - listRect.bottom;
  }, [activeIndex]);

  const moveActive = useCallback((direction) => {
    const now = Date.now();
    if (now - lastArrowAtRef.current < 60) return;
    lastArrowAtRef.current = now;
    if (!permissionsLoadedRef.current) return;
    const len = resultsRef.current.length;
    if (len === 0) return;
    // If nothing selected yet, ↓ starts at 0
    const current = activeIndexRef.current;
    const next = nextSearchActiveIndex(current, direction, len);
    setActive(next);
  }, [setActive]);

  const choose = useCallback((item) => {
    if (!item) return;
    if (item.department && typeof onSelectDepartment === 'function') {
      onSelectDepartment(item.department);
    }
    dispatchMenuNavigate(item.id, { department: item.department });
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  }, [onSelectDepartment]);

  const fontSize = compact ? '0.75rem' : '0.9rem';

  return (
    <div
      ref={wrapRef}
      data-tutorial="menu-search"
      style={{
        position: 'relative',
        flex: compact ? '1 1 auto' : '0 1 auto',
        // minWidth 0 (not 180) lets the field give ground instead of forcing the
        // whole header row wider than the viewport.
        minWidth: 0,
        marginLeft: compact ? 0 : 8,
        marginRight: compact ? 0 : 8
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 4 : 6,
          height: compact ? 36 : 44,
          padding: compact ? '0 0.5rem' : '0 1.25rem',
          background: 'transparent',
          borderRadius: 8,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          // 150 rather than 180 on the compact field: the header's right-hand cluster
          // cannot shrink, so every pixel the search takes is taken from the project
          // switcher on its left, which was being squeezed down to a bare icon.
          // The phone top bar only holds the logo, search and hamburger now that the
          // context switchers moved to their own stacked row, so the search gets real
          // width back instead of collapsing to an icon.
          maxWidth: compact ? 200 : 280,
          width: compact ? '100%' : 260,
          boxSizing: 'border-box'
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ display: 'block', flexShrink: 0, color: 'white' }}
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>

        <input
          ref={inputRef}
          value={query}
          placeholder={compact ? 'Search...' : 'Search menu...'}
          aria-label="Search menu"
          aria-expanded={showDropdown}
          aria-controls="header-menu-search-results"
          aria-activedescendant={activeIndex >= 0 ? `header-menu-opt-${activeIndex}` : undefined}
          autoComplete="off"
          spellCheck={false}
          onFocus={() => {
            setOpen(true);
            ensurePermissions();
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            ensurePermissions();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              if (query) setQuery('');
              else {
                setOpen(false);
                inputRef.current?.blur();
              }
              return;
            }
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              e.stopPropagation();
              if (e.repeat) return;
              setOpen(true);
              moveActive(e.key === 'ArrowDown' ? 'down' : 'up');
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              const list = resultsRef.current;
              const idx = activeIndexRef.current < 0 ? 0 : activeIndexRef.current;
              if (list[idx]) choose(list[idx]);
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'white',
            caretColor: 'white',
            padding: 0,
            margin: 0,
            fontSize,
            fontWeight: 500,
            letterSpacing: '0.2px',
            fontFamily: 'inherit'
          }}
        />

        {!compact && !query && (
          <kbd
            style={{
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.2px',
              lineHeight: 1,
              padding: '3px 6px',
              borderRadius: 5,
              border: '1px solid rgba(255,255,255,0.25)',
              background: 'transparent',
              color: 'white',
              fontFamily: 'inherit'
            }}
          >
            {MOD_HINT}
          </kbd>
        )}
      </div>

      {showDropdown && createPortal(
        <div
          id="header-menu-search-results"
          role="listbox"
          ref={listRef}
          style={{
            position: 'fixed',
            top: panelPos.top,
            left: panelPos.left,
            width: panelPos.width,
            maxHeight: 'min(60vh, 420px)',
            overflowY: 'auto',
            background: '#fff',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.22)',
            zIndex: 9999,
            padding: '4px 0'
          }}
        >
          {!permissionsLoaded && (
            <div style={{ padding: '12px 14px', color: '#64748b', fontSize: 13 }}>Loading…</div>
          )}
          {permissionsLoaded && results.length === 0 && (
            <div style={{ padding: '12px 14px', color: '#64748b', fontSize: 13 }}>
              No menu items match “{query.trim()}”.
            </div>
          )}
          {permissionsLoaded && results.map((item, idx) => {
            const active = idx === activeIndex;
            return (
              <button
                key={`${item.department || 'x'}:${item.id}:${item.path}`}
                id={`header-menu-opt-${idx}`}
                type="button"
                role="option"
                aria-selected={active}
                data-idx={idx}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(item)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '9px 12px',
                  background: active ? '#fee2e2' : 'transparent',
                  borderLeft: active ? '3px solid #dc3545' : '3px solid transparent'
                }}
              >
                <div style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  {item.icon && <span aria-hidden="true">{item.icon}</span>}
                  <span>{item.label}</span>
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {item.path}
                  {item.department ? ` · ${item.department}` : ''}
                  {item.needsProject ? ' · needs project' : ''}
                  {item.needsEstimate ? ' · needs estimate' : ''}
                </div>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export default HeaderSearchBox;
